import React, { ReactNode, useEffect, useState } from 'react';
import { RadioChangeEvent } from 'antd';
import { useTranslation } from 'react-i18next';

import IbButton from '../IbButton';
import IbIcon, { IbIconName } from '../IbIcon';
import IbModal, { IbModalPosition } from '../IbModal';
import IbRadioButton from '../IbRadioButton';
import IbRadioGroup from '../IbRadioGroup';
import IbTypography from '../IbTypography';

import './index.less';

import {
  CONTENT_CLASS_NAME,
  MAIN_CLASS_NAME,
  TYPE_GRID_CLASS_NAME,
  TYPE_OPTION_CLASS_NAME,
  TYPE_OPTION_LABEL_CLASS_NAME,
  TYPE_OPTION_TITLE_CLASS_NAME,
} from './const';

export enum TypedStepModalStep {
  TypeSelection = 'TypeSelection',
  Parameters = 'Parameters',
}

export interface ITypedStepTypeOption<TType extends string> {
  description: string;
  iconName: IbIconName;
  title: string;
  value: TType;
}

export interface ITypedStepTypeSelectionProps<TType extends string> {
  options: ITypedStepTypeOption<TType>[];
  value?: TType;
  onChange: (event: RadioChangeEvent) => void;
}

export const TypedStepTypeSelection = <TType extends string>({
  options,
  value,
  onChange,
}: ITypedStepTypeSelectionProps<TType>): React.ReactElement => (
  <IbRadioGroup
    buttonStyle="outline"
    className={TYPE_GRID_CLASS_NAME}
    optionType="default"
    value={value}
    onChange={onChange}
  >
    {options.map((option) => (
      <IbRadioButton key={option.value} showCircle className={TYPE_OPTION_CLASS_NAME} value={option.value}>
        <div className={TYPE_OPTION_LABEL_CLASS_NAME}>
          <div className={TYPE_OPTION_TITLE_CLASS_NAME}>
            <IbIcon iconName={option.iconName} size={20} />
            <IbTypography.Paragraph strong>{option.title}</IbTypography.Paragraph>
          </div>
          <IbTypography.Paragraph disabled type="secondary">
            {option.description}
          </IbTypography.Paragraph>
        </div>
      </IbRadioButton>
    ))}
  </IbRadioGroup>
);

export interface IUseTypedStepModalStateOptions<TStep> {
  isCreation?: boolean;
  step?: TStep;
  visible: boolean;
  onReset?: (step?: TStep) => void;
}

export interface IUseTypedStepModalState<TStep> {
  draftStep?: TStep;
  modalStep: TypedStepModalStep;
  saving: boolean;
  setDraftStep: React.Dispatch<React.SetStateAction<TStep | undefined>>;
  setModalStep: React.Dispatch<React.SetStateAction<TypedStepModalStep>>;
  setSaving: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useTypedStepModalState = <TStep,>({
  isCreation = false,
  step,
  visible,
  onReset,
}: IUseTypedStepModalStateOptions<TStep>): IUseTypedStepModalState<TStep> => {
  const [draftStep, setDraftStep] = useState<TStep | undefined>(step);
  const [saving, setSaving] = useState(false);
  const [modalStep, setModalStep] = useState<TypedStepModalStep>(TypedStepModalStep.TypeSelection);

  useEffect(() => {
    if (!visible) {
      return;
    }

    setDraftStep(step);
    setSaving(false);
    setModalStep(isCreation ? TypedStepModalStep.TypeSelection : TypedStepModalStep.Parameters);
    onReset?.(step);
  }, [isCreation, step, visible]);

  return {
    draftStep,
    modalStep,
    saving,
    setDraftStep,
    setModalStep,
    setSaving,
  };
};

export interface ITypedStepModalProps {
  canGoNext: boolean;
  modalStep: TypedStepModalStep;
  parameters: ReactNode;
  saving: boolean;
  title: ReactNode;
  typeSelection: ReactNode;
  visible: boolean;
  width?: number;
  onClose: () => void;
  onNext: () => void;
  onSave: () => void;
}

const TypedStepModal: React.FC<ITypedStepModalProps> = ({
  canGoNext,
  modalStep,
  parameters,
  saving,
  title,
  typeSelection,
  visible,
  width,
  onClose,
  onNext,
  onSave,
}) => {
  const { t } = useTranslation();
  const isTypeSelectionStep = modalStep === TypedStepModalStep.TypeSelection;
  const footer = isTypeSelectionStep ? (
    <>
      <IbButton disabled={!canGoNext} onClick={onNext}>
        {t('Agent: Toolset: Modal: Next')}
      </IbButton>
      <IbButton type="secondary" onClick={onClose}>
        {t('Cancel')}
      </IbButton>
    </>
  ) : (
    <>
      <IbButton loading={saving} onClick={onSave}>
        {t('Save')}
      </IbButton>
      <IbButton type="secondary" onClick={onClose}>
        {t('Cancel')}
      </IbButton>
    </>
  );

  return (
    <IbModal
      className={MAIN_CLASS_NAME}
      footer={footer}
      position={IbModalPosition.Centered}
      title={title}
      visible={visible}
      width={width}
      onCancel={onClose}
    >
      <div className={CONTENT_CLASS_NAME}>{isTypeSelectionStep ? typeSelection : parameters}</div>
    </IbModal>
  );
};

export default TypedStepModal;
